import {useEffect, useState} from 'react';
import Button from 'components/ui/Button';
import Dialog from '@mui/material/Dialog';
import Spinner from 'components/ui/Spinner';
import TextField from 'components/ui/TextField';
import Locations from '../locations/Locations';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import {
    useDeleteClassMutation,
    useGetClassQuery,
    useGetTruckAvailabilityQuery,
    useGetTruckOverrideByTruckIdQuery,
    useUpdateClassMutation
} from 'services/classes';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import * as Styles from 'layouts/PrivateLayout.styles';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import {TAB_SETTINGS, TAB_TIME_ALLOCATION} from './constants';
import Tab from '@mui/material/Tab';
import {a11yProps, TabPanel} from 'layouts/components/Tabs';
import TimeAllocationCalendar from '../locations/components/TimeAllocationCalendar';
import {format} from 'date-fns';
import {trucktypes} from "../../../constants/trucktypes";

const EditTruckModule = () => {
    const {classId = ''} = useParams();
    const navigate = useNavigate();

    const {data: classData, isLoading, isFetching, refetch: refetchClass} = useGetClassQuery(classId);
    const [updateClass] = useUpdateClassMutation();
    const [deleteClass] = useDeleteClassMutation();
    const [className, setClassName] = useState('');
    const [truckType, setTruckType] = useState<number | string>(''); // New state for Truck Type
    const [linkedProduct, setLinkedProduct] = useState<any>(null);
    const [additionalInformation, setAdditionalInformation] = useState<any>([]);
    const [locations, setLocations] = useState<readonly number[]>();

    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    // Fetch availability data for the truck
    const {data: availabilityData, refetch: refetchAvailability}: any = useGetTruckAvailabilityQuery({
        truckId: classId,
        month: currentMonth,
        year: currentYear
    });

    const [trucksTab, setTrucksTab] = useState(TAB_TIME_ALLOCATION);
    const [openConfirm, setOpenConfirm] = useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const disableSubmit = !className.length || truckType === ''; // Ensure truck type is selected

    const locationsColumns = [{field: 'name', headerName: 'Location'}];

    useEffect(() => {
        if (classData?.class) {
            setClassName(classData.class.name);
            setTruckType(classData.class.truck_type || ''); // Set the truck type from API data

            const activeLocations = classData.class.locations?.filter((location: any) => location.status);
            const dataLocations = activeLocations?.map((location: any) => location.id);
            setLocations(dataLocations);

            // Set the linked product if it exists
            if (classData.class.variant) {
                setLinkedProduct({
                    id: classData.class.variant.id,
                    displayName: classData.class.variant.displayName,
                    title: classData.class.variant.title,
                    sku: classData.class.variant.sku,
                    price: classData.class.variant.price,
                    product: {
                        title: classData.class.variant.product.title,
                        featuredImage: {
                            url: classData.class.variant.product.featuredImage?.url || ''
                        }
                    }
                });
            }
            console.log(classData.class.truck_type)
            // Preselect the truck type
            setTruckType(classData.class.truck_type.toString() || ''); // Ensure the fetched `truck_type` value is set

            setDataLoaded(true);
        }
    }, [classData?.class]);

    const {data: overrideData}: any = useGetTruckOverrideByTruckIdQuery(classId);

    const events = availabilityData?.length
        ? Object.entries(availabilityData[0].availability || {}).flatMap(([date, trucks]: any) =>
            trucks.map((truck: any) => {
                // Parse the date
                const [month, day, year] = date.split('-');
                const formattedDate = `${year}-${month}-${day}`;

                // Find override for the given date
                const override = overrideData?.find((override: any) =>
                    override.date_of_override.split('T')[0] === formattedDate
                );

                return {
                    title: `Truck ${truck.truck_name} / ${truck.truck_type} - ${override ? override.override_minutes : truck.minutes_available} min`,
                    start: format(new Date(date), 'yyyy-MM-dd'), // FullCalendar requires ISO format
                    allDay: true,
                    backgroundColor: !!override ? '#FF0000' : '#91D0FF', // Differentiate override days
                    borderColor: !!override ? '#FF0000' : '#91D0FF',
                    extendedProps: {
                        truckName: truck.truck_name,
                        truckId: truck.truck_id,
                        minutesAvailable: override ? override.override_minutes : truck.minutes_available,
                        capacity: truck.capacity,
                        locationId: truck.location_id,
                        overrideExists: !!override, // Boolean flag for override existence
                        overrideId: override?.id || "", // Safely handle override ID
                    },
                };
            })
        )
        : []; // Default to an empty array if availabilityData is missing

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = {
            classId,
            body: {
                name: className,
                truck_type: truckType,
                location_active_ids: locations,
            },
        };

        try {
            await updateClass(data);
            enqueueSnackbar('Truck successfully edited', {
                variant: 'success',
                autoHideDuration: 1000
            });
            await refetchClass();
        } catch (error) {
            enqueueSnackbar('Failed to update class. Please try again.', {
                variant: 'error',
                autoHideDuration: 1000
            });
        }
    };

    const handleCloseConfirm = () => {
        setOpenConfirm(false);
    };

    const handleOpenConfirm = () => {
        setOpenConfirm(true);
    };

    const handleDeleteClass = () => {
        setOpenConfirm(false);
        onDelete();
    };

    const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
        setTrucksTab(newValue);
    };

    const onDelete = async () => {
        try {
            await deleteClass(classId);
            enqueueSnackbar('Class deleted', {
                variant: 'success',
                autoHideDuration: 1000
            });
            goToIndex();
        } catch (error) {
            enqueueSnackbar('Class delete error, please try again', {
                variant: 'error',
                autoHideDuration: 1000
            });
        }
    };

    const goToIndex = () => {
        navigate('/admin/classes');
    };

    return (
        <div>
            <Styles.CreateEditPageTitleContainer>Edit Truck</Styles.CreateEditPageTitleContainer>
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                <Tabs value={trucksTab} onChange={handleChangeTab} aria-label="Truck tabs">
                    <Tab label="Time Allocation" {...a11yProps(TAB_TIME_ALLOCATION)} />
                    <Tab label="Settings" {...a11yProps(TAB_SETTINGS)} />
                </Tabs>
            </Box>
            <TabPanel value={trucksTab} index={TAB_TIME_ALLOCATION}>
                <div>
                    <TimeAllocationCalendar events={events} refetchEvents={refetchAvailability}/>
                </div>
            </TabPanel>
            <TabPanel value={trucksTab} index={TAB_SETTINGS}>
                <div>
                    {isLoading && isFetching ? (
                        <Spinner/>
                    ) : (
                        <>
                            <form onSubmit={handleSubmit}>
                                <Styles.FormColumns>
                                    <Styles.FormColumnLeft>
                                        <Styles.FormColumnLeftDescription>
                                            <strong>Truck Name*</strong>
                                        </Styles.FormColumnLeftDescription>
                                    </Styles.FormColumnLeft>
                                    <Styles.FormColumnRight>
                                        <TextField
                                            id="className"
                                            required
                                            cc-width="full"
                                            label="Truck name"
                                            value={className}
                                            onChange={(e) => setClassName(e.target.value)}
                                        />
                                    </Styles.FormColumnRight>
                                </Styles.FormColumns>
                                <Styles.FormColumns>
                                    <Styles.FormColumnLeft>
                                        <Styles.FormColumnLeftDescription>
                                            <strong>Truck Type*</strong>
                                        </Styles.FormColumnLeftDescription>
                                    </Styles.FormColumnLeft>
                                    <Styles.FormColumnRight>
                                        <TextField
                                            id="truckType"
                                            select
                                            required
                                            cc-width="full"
                                            value={truckType}
                                            onChange={(e) => setTruckType(Number(e.target.value))}
                                            SelectProps={{
                                                native: true,
                                            }}
                                        >
                                            <option value="">Select Truck Type</option>
                                            {Object.entries(trucktypes).map(([value, label]) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Styles.FormColumnRight>
                                </Styles.FormColumns>

                                {dataLoaded && (
                                    <Locations
                                        title="Location Availability"
                                        tableColumns={locationsColumns}
                                        showButtons={false}
                                        navigateToLocation={false}
                                        currentLocations={locations}
                                        setSelectedLocations={setLocations}
                                    />
                                )}

                                <div className="mt-4">
                                    <Button cc-color="red" type="button" onClick={handleOpenConfirm}>
                                        Delete
                                    </Button>
                                    <Button className="float-right mr-4" cc-color="purple" type="submit"
                                            disabled={disableSubmit}>
                                        Save
                                    </Button>
                                    <Link className="float-right mr-4" to={'/admin/classes'}>
                                        <Button>Cancel</Button>
                                    </Link>
                                </div>
                            </form>
                            <Dialog open={openConfirm} onClose={handleCloseConfirm} aria-labelledby="Delete Class">
                                <DialogTitle id={`alert-dialog-title`}>{'Are you sure?'}</DialogTitle>
                                <DialogContent>
                                    <DialogContentText id={`alert-dialog-description`}>
                                        Are you sure you want to delete this class?
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseConfirm}>Cancel</Button>
                                    <button className="bg-primary-500 text-white px-3 py-2 rounded-lg"
                                            onClick={handleDeleteClass}>
                                        Remove
                                    </button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )}
                </div>
            </TabPanel>
        </div>
    );
};

export default EditTruckModule;
